import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FuelPlanResponseStatus } from "enums/fuelPlanResponseStatus";
import { useHistory } from "react-router";
import {
  useDispatchService,
  useManagerService,
  useParamService,
} from "services";
import { withSelectedFuelStop } from "services/fuel-plan-selection";
import {
  createResponseSnapshot,
  createFailureSnapshot,
} from "services/fuel-plan-feedback";
import { FuelPlanFeedbackSnapshot } from "types/fuelPlanFeedback";
import type { FuelPlanFeedbackLayout } from "types/fuelPlanFeedbackLayout";
import { FuelPlanFuelStop, FuelPlanResponse, FuelPlanStop } from "types";
import {
  createClosedFuelStopModal,
  createFuelStopModal,
  FuelPlanLoading,
  FuelPlanTimeline,
  FuelPlanSurvey,
  FuelPlanUnavailable,
  FuelStopModal,
  FuelStopModalState,
  TargetGallonsCalculator,
} from "components/fuel-plan";
import {
  CalculatorInputField,
  CalculatorInputState,
  CalculatorState,
  calculateTargetForCurrentValues,
  createInitialCalculatorState,
  EMPTY_CALCULATOR_INPUTS,
  EMPTY_CALCULATOR_STATE,
  formatCalculatorInput,
  isValidCalculatorInput,
  parseCalculatorInput,
  updateAvailableFuelRange,
  updateCalculatorLegMiles,
} from "./fuel-plan-calculator";
import {
  getCalculatorStopForFuelSelection,
  getDefaultCalculatorStop,
  getCalculatorLeg,
  getInitialSelectedFuelStops,
  getPlannedFuelStops,
  SelectedFuelStops,
} from "./fuel-plan-display";
import {
  buildFuelPlanMapUrl,
  buildFuelPlanOverviewMapUrl,
  buildFuelPlanReturnUrl,
  parseFuelPlanReturnSelection,
} from "./fuel-plan-map-navigation";
import "./fuel-plan.scss";

const FuelPlan = () => {
  const history = useHistory();
  const { getStringValue } = useParamService();
  const { getFuelPlanByLoadNumber } = useDispatchService();
  const {
    logFuelPlanAccess,
    getFuelPlanSurveyStatus,
    submitFuelPlanSurvey,
  } = useManagerService();
  const [loadNumber] = useState(getStringValue("loadNumber") ?? "");
  const [fuelPlan, setFuelPlan] = useState<FuelPlanResponse | null>(null);
  const [
    attemptSnapshot,
    setAttemptSnapshot,
  ] = useState<FuelPlanFeedbackSnapshot | null>(null);
  const [selectedFuelStops, setSelectedFuelStops] = useState<SelectedFuelStops>(
    {}
  );
  const [
    selectedCalculatorStop,
    setSelectedCalculatorStop,
  ] = useState<FuelPlanStop | null>(null);
  const [calculator, setCalculator] = useState<CalculatorState>(
    EMPTY_CALCULATOR_STATE
  );
  const [calculatorInputs, setCalculatorInputs] = useState<
    CalculatorInputState
  >(EMPTY_CALCULATOR_INPUTS);
  const [fuelStopModal, setFuelStopModal] = useState<FuelStopModalState>(
    createClosedFuelStopModal
  );
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const calculatorRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const requestCancellation = axios.CancelToken.source();
    let isPageActive = true;

    const loadFuelPlan = async () => {
      if (!loadNumber) {
        setErrorMessage("Missing load number.");
        setIsLoading(false);
        return;
      }

      const attemptedAtUtc = new Date().toISOString();

      try {
        const fetchedFuelPlan = await getFuelPlanByLoadNumber(
          loadNumber,
          requestCancellation.token,
          (response) => {
            if (isPageActive)
              setAttemptSnapshot(
                createResponseSnapshot(response, attemptedAtUtc)
              );
          }
        );
        if (!isPageActive) return;

        if (fetchedFuelPlan.status !== FuelPlanResponseStatus.Ok) {
          setFuelPlan(fetchedFuelPlan);
          return;
        }

        const returnedMapSelection = parseFuelPlanReturnSelection(
          window.location.search,
          loadNumber
        );
        const resolvedFuelPlan = returnedMapSelection
          ? withSelectedFuelStop(
              fetchedFuelPlan,
              returnedMapSelection.sequence,
              returnedMapSelection.fuelStopId
            )
          : fetchedFuelPlan;

        if (returnedMapSelection) {
          history.replace(buildFuelPlanReturnUrl(loadNumber));
        }

        const initialFuelStopSelections = getInitialSelectedFuelStops(
          resolvedFuelPlan.stops
        );
        const initialCalculatorStop = returnedMapSelection
          ? getCalculatorStopForFuelSelection(
              resolvedFuelPlan.stops,
              returnedMapSelection.sequence,
              returnedMapSelection.fuelStopId
            )
          : getDefaultCalculatorStop(resolvedFuelPlan.stops);
        const initialCalculatorLeg = initialCalculatorStop
          ? getCalculatorLeg(
              initialCalculatorStop,
              resolvedFuelPlan.stops,
              initialFuelStopSelections
            )
          : null;
        const initialCalculator = createInitialCalculatorState(
          resolvedFuelPlan.calculator,
          initialCalculatorLeg?.miles ?? 0,
          Boolean(initialCalculatorStop)
        );

        setFuelPlan(resolvedFuelPlan);
        setSelectedFuelStops(initialFuelStopSelections);
        setSelectedCalculatorStop(initialCalculatorStop ?? null);
        setCalculator(initialCalculator.values);
        setCalculatorInputs(initialCalculator.inputs);
      } catch (error: any) {
        if (!isPageActive || axios.isCancel(error)) return;

        console.error("Unable to load fuel plan.", error);
        const displayedMessage =
          error?.response?.data?.message ??
          error?.message ??
          "Unable to load fuel plan.";
        setErrorMessage(displayedMessage);
        setAttemptSnapshot(
          createFailureSnapshot(
            error?.response?.status,
            displayedMessage,
            attemptedAtUtc
          )
        );
      } finally {
        if (!isPageActive) return;

        try {
          await logFuelPlanAccess(loadNumber);
        } catch (logError) {
          console.warn("Fuel plan access log failed.", logError);
        }

        setIsLoading(false);
      }
    };

    loadFuelPlan();

    return () => {
      isPageActive = false;
      requestCancellation.cancel("Fuel plan page was closed.");
    };
  }, [loadNumber]);

  const scrollCalculatorIntoViewOnMobile = () => {
    if (!window.matchMedia("(max-width: 900px)").matches) return;

    window.requestAnimationFrame(() => {
      calculatorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleCalculatorLegSelection = (timelineStop: FuelPlanStop) => {
    if (!fuelPlan) return;

    const displayedLeg = getCalculatorLeg(
      timelineStop,
      fuelPlan.stops,
      selectedFuelStops
    );
    if (!displayedLeg) return;

    setSelectedCalculatorStop(timelineStop);
    scrollCalculatorIntoViewOnMobile();
    setCalculatorInputs((currentInputs) => ({
      ...currentInputs,
      milesToNextStop: formatCalculatorInput(displayedLeg.miles),
    }));
    setCalculator((currentCalculator) =>
      updateCalculatorLegMiles(currentCalculator, displayedLeg.miles)
    );
  };

  const handleAvailableRangeChange = (value: string) => {
    if (!isValidCalculatorInput(value)) return;

    const rangeMiles = parseCalculatorInput(value);
    setCalculatorInputs((currentInputs) => ({
      ...currentInputs,
      currentFuelRangeMiles: value,
    }));
    setCalculator((currentCalculator) =>
      updateAvailableFuelRange(currentCalculator, rangeMiles)
    );
  };

  const handleCalculatorInputChange = (
    field: CalculatorInputField,
    value: string
  ) => {
    if (!isValidCalculatorInput(value)) return;

    setCalculatorInputs((currentInputs) => ({
      ...currentInputs,
      [field]: value,
    }));
    setCalculator((currentCalculator) => ({
      ...currentCalculator,
      [field]: parseCalculatorInput(value),
    }));
  };

  const handleCalculate = () => {
    if (!selectedCalculatorStop) return;

    setCalculator(calculateTargetForCurrentValues);
  };

  const closeFuelStopModal = useCallback(() => {
    setFuelStopModal((currentModal) => ({
      ...currentModal,
      isOpen: false,
    }));
  }, []);

  const handleViewFuelStops = (recommendation: FuelPlanStop) => {
    setFuelStopModal(createFuelStopModal(recommendation));
  };

  const handleViewFuelStopsOnMap = (
    recommendationSequence: number,
    fuelStops: FuelPlanFuelStop[]
  ) => {
    const mapUrl = buildFuelPlanMapUrl({
      loadNumber,
      sequence: recommendationSequence,
      fuelStops,
      selectedFuelStopId: selectedFuelStops[recommendationSequence]?.id,
    });

    if (mapUrl) history.push(mapUrl);
  };

  const handleViewPlannedFuelStops = () => {
    if (!fuelPlan) return;

    const mapUrl = buildFuelPlanOverviewMapUrl({
      loadNumber,
      fuelStops: getPlannedFuelStops(fuelPlan.stops, selectedFuelStops),
    });

    if (mapUrl) history.push(mapUrl);
  };

  const handleFuelStopSelection = (
    recommendationSequence: number,
    fuelStop: FuelPlanFuelStop
  ) => {
    if (!fuelPlan) return;

    const updatedFuelPlan = withSelectedFuelStop(
      fuelPlan,
      recommendationSequence,
      fuelStop.id
    );
    const selectedFuelStop = updatedFuelPlan.stops
      .find((timelineStop) => timelineStop.sequence === recommendationSequence)
      ?.fuelStops.find((candidate) => candidate.isDefault);

    if (!selectedFuelStop) {
      closeFuelStopModal();
      return;
    }

    const updatedFuelStopSelections = {
      ...selectedFuelStops,
      [recommendationSequence]: selectedFuelStop,
    };

    setFuelPlan(updatedFuelPlan);
    setSelectedFuelStops(updatedFuelStopSelections);

    const shouldUpdateCalculator =
      selectedCalculatorStop?.sequence === recommendationSequence &&
      selectedCalculatorStop.nextLeg?.isCalculatorEligible;

    if (shouldUpdateCalculator) {
      const updatedCalculatorLeg = getCalculatorLeg(
        selectedCalculatorStop,
        updatedFuelPlan.stops,
        updatedFuelStopSelections
      );

      if (updatedCalculatorLeg) {
        setCalculatorInputs((currentInputs) => ({
          ...currentInputs,
          milesToNextStop: formatCalculatorInput(updatedCalculatorLeg.miles),
        }));
        setCalculator((currentCalculator) =>
          updateCalculatorLegMiles(
            currentCalculator,
            updatedCalculatorLeg.miles
          )
        );
      }
    }

    closeFuelStopModal();
  };

  if (isLoading) {
    return <FuelPlanLoading />;
  }

  const selectedFuelStopIds = Object.keys(selectedFuelStops).reduce<
    Record<string, string>
  >((ids, sequence) => {
    ids[sequence] = String(selectedFuelStops[Number(sequence)].id);
    return ids;
  }, {});
  const renderFeedback = (
    renderLayout?: FuelPlanFeedbackLayout
  ) =>
    attemptSnapshot && (
      <FuelPlanSurvey
        key={`${loadNumber}:${attemptSnapshot.generationId ?? attemptSnapshot.attemptedAtUtc}`}
        loadNumber={loadNumber}
        snapshot={{ ...attemptSnapshot, selectedFuelStopIds }}
        checkStatus={() => getFuelPlanSurveyStatus(loadNumber, attemptSnapshot.generationId)}
        submitSurvey={submitFuelPlanSurvey}
        renderLayout={renderLayout}
      />
    );

  if (errorMessage) {
    return (
      <FuelPlanUnavailable message={errorMessage} feedback={renderFeedback()} />
    );
  }

  if (!fuelPlan || fuelPlan.status !== FuelPlanResponseStatus.Ok) {
    return (
      <FuelPlanUnavailable
        feedback={renderFeedback()}
        message={
          fuelPlan?.message ?? "No fuel plan is available for this load."
        }
      />
    );
  }

  return (
    <>
      <div className="fuel-plan-page">
        <div className="fuel-plan-shell">
          <FuelPlanTimeline
            fuelPlan={fuelPlan}
            feedback={attemptSnapshot ? renderFeedback : undefined}
            onSelectCalculatorLeg={handleCalculatorLegSelection}
            onViewFuelStops={handleViewFuelStops}
            onViewPlannedFuelStops={handleViewPlannedFuelStops}
            selectedCalculatorStop={selectedCalculatorStop}
            selectedFuelStops={selectedFuelStops}
          />
          <TargetGallonsCalculator
            calculator={calculator}
            calculatorInputs={calculatorInputs}
            calculatorRef={calculatorRef}
            maxFuelRangeMiles={fuelPlan.calculator?.maxFuelRangeMiles ?? null}
            onAvailableRangeChange={handleAvailableRangeChange}
            onCalculate={handleCalculate}
            onCalculatorInputChange={handleCalculatorInputChange}
            selectedCalculatorStop={selectedCalculatorStop}
            selectedFuelStops={selectedFuelStops}
            timelineStops={fuelPlan.stops}
          />
        </div>
      </div>
      <FuelStopModal
        modal={fuelStopModal}
        onClose={closeFuelStopModal}
        onSelectFuelStop={handleFuelStopSelection}
        onViewFuelStopsOnMap={handleViewFuelStopsOnMap}
        selectedFuelStops={selectedFuelStops}
      />
    </>
  );
};

export default FuelPlan;
